import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { dailyCompletionApi } from '../services/apiV2';

interface StudentData {
  student_id: number;
  student_email: string;
  student_first_name: string;
  student_last_name: string;
  dates: {
    [date: string]: {
      live_completed: number;
      live_assigned: number;
      makeup_completed: number;
      makeup_assigned: number;
      makeup_started?: number;
      external_completed: number;
      external_assigned: number;
      completion_status: 'complete' | 'partial' | 'missing';
      needs_review: boolean;
      quality_flags: string[];
      daily_work?: { plan_status: string; wrapup_status: string; plan: string; wrap_up: string; result: string;
        evidence_url?: string; attachments?: Array<{ name: string; url: string }> } | null;
    };
  };
}

interface MatrixData {
  section_id: number;
  section_name: string;
  dates: string[];
  students: {
    [studentId: string]: StudentData;
  };
  assignment_boards?: AssignmentBoard[];
  total_assigned: number;
  total_started?: number;
  total_completed: number;
  total_not_completed: number;
  completion_rate: number;
  daily_work_summary?: { expected_days: number; plans_submitted: number; wrapups_submitted: number; wrapups_missing: number; excused_days: number };
}

interface AssignmentBoardStudent {
  student_id: number;
  student_name: string;
  student_first_name: string;
  student_last_name: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'excused' | string;
  started_at: string | null;
  completed_at: string | null;
  response_count: number;
  ai_event_count: number;
}

interface AssignmentBoard {
  assignment_id: number;
  lesson_plan_id: number;
  lesson_plan_title: string;
  delivery_mode: string;
  assigned_at: string | null;
  due_at: string | null;
  report_date: string | null;
  assigned_count: number;
  started_count: number;
  completed_count: number;
  buckets: {
    not_started?: AssignmentBoardStudent[];
    in_progress?: AssignmentBoardStudent[];
    completed?: AssignmentBoardStudent[];
    excused?: AssignmentBoardStudent[];
  };
}

interface DailyCompletionMatrixProps {
  sectionId: number;
  startDate: string;
  endDate: string;
  showIncompleteOnly?: boolean;
}

const DailyCompletionMatrix: React.FC<DailyCompletionMatrixProps> = ({
  sectionId,
  startDate,
  endDate,
  showIncompleteOnly = false,
}) => {
  const [data, setData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const requestVersion = useRef(0);

  useEffect(() => {
    loadMatrix();
    return () => { requestVersion.current += 1; };
  }, [sectionId, startDate, endDate, showIncompleteOnly]);

  const loadMatrix = async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    try {
      const response = await dailyCompletionApi.matrix({
        section_id: sectionId,
        start_date: startDate,
        end_date: endDate,
        show_incomplete_only: showIncompleteOnly,
      });
      if (version === requestVersion.current) setData(response.data);
    } catch (err: any) {
      console.error('Error loading matrix:', err);
      if (version === requestVersion.current) setError(err.response?.data?.error || 'Failed to load completion matrix');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = String(dateStr).split('-').map(Number);
    const date = year && month && day ? new Date(year, month - 1, day) : new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getCellClass = (status: string, needsReview: boolean) => {
    if (needsReview) return 'bg-red-50 border-red-200';
    if (status === 'complete') return 'bg-green-50 border-green-200';
    if (status === 'partial') return 'bg-yellow-50 border-yellow-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getCompletionText = (dateData: any) => {
    const totalAssigned =
      dateData.live_assigned + dateData.makeup_assigned + dateData.external_assigned;
    const totalCompleted =
      dateData.live_completed + dateData.makeup_completed + dateData.external_completed;

    if (totalAssigned === 0) return '-';
    return `${totalCompleted}/${totalAssigned}`;
  };

  const getTypeBreakdown = (dateData: any) => {
    const parts = [];
    if (dateData.live_assigned > 0) {
      parts.push(`L ${dateData.live_completed}/${dateData.live_assigned}`);
    }
    if (dateData.makeup_assigned > 0) {
      parts.push(`M ${dateData.makeup_completed}/${dateData.makeup_assigned}`);
    }
    if (dateData.external_assigned > 0) {
      parts.push(`E ${dateData.external_completed}/${dateData.external_assigned}`);
    }
    return parts.join(' | ');
  };

  const getQualityFlagLabel = (flag: string) => {
    const labels: { [key: string]: string } = {
      blank_submission: 'Blank',
      missing_reasoning: 'No Reasoning',
      rapid_submission: 'Too Fast',
      multiple_attempts: 'Many Attempts',
      quality_flagged: 'Flagged',
      external_no_evidence: 'No Evidence',
      low_quality_reasoning: 'Low Quality',
      superficial_understanding: 'Superficial',
      conceptual_gaps: 'Concept Gap',
      weak_evidence: 'Weak Evidence',
      disorganized_thought: 'Disorganized',
    };
    return labels[flag] || flag;
  };

  const assignmentBoards = data?.assignment_boards || [];
  const statusColumns: Array<{
    key: 'not_started' | 'in_progress' | 'completed' | 'excused';
    label: string;
    className: string;
  }> = [
    { key: 'not_started', label: 'To Do', className: 'border-gray-200 bg-gray-50' },
    { key: 'in_progress', label: 'In Progress', className: 'border-amber-200 bg-amber-50' },
    { key: 'completed', label: 'Complete', className: 'border-green-200 bg-green-50' },
    { key: 'excused', label: 'Excused', className: 'border-rose-200 bg-rose-50' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading completion matrix...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">{error}</div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const studentEntries = Object.entries(data.students);
  const daily = data.daily_work_summary;
  const dailyLabel = (value: string) => ({ submitted: 'Submitted', missing: 'Missing', in_progress: 'Not yet submitted',
    excused: 'Excused', upcoming: 'Upcoming', not_expected: 'Not required' }[value] || value);

  return (
    <div className="space-y-4">
      {daily && (daily.expected_days > 0 || daily.excused_days > 0 || studentEntries.some(([, student]) => Object.values(student.dates).some(day => day.daily_work))) && <section aria-label="Daily goal work" className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm">
        <h3 className="font-semibold">Daily goal work</h3>
        <p className="mt-2">Plans submitted: {daily.plans_submitted}/{daily.expected_days} · Wrap-ups submitted: {daily.wrapups_submitted}/{daily.expected_days} · Past wrap-ups missing: {daily.wrapups_missing} · Excused days: {daily.excused_days}</p>
        <p className="mt-1 text-xs text-gray-600">Student-reported participation, not proficiency. Today’s wrap-up may still be in progress. Absences, teacher bypasses, non-school days, and days before the goal began are not required.</p>
      </section>}
      {/* Summary Stats */}
      <p className="text-sm text-gray-600">Counts lesson, assignment, and external-task records separately, including linked make-up records. Assignments of all purposes use due date (or assigned date if none) in the class timezone; archived and excused assignments are excluded. Class Overview instead counts an original lesson once and lets completed make-up satisfy it. Completion is not proficiency.</p>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-5">
          <div>
            <div className="text-2xl font-bold text-gray-900">{data.total_assigned}</div>
            <div className="text-sm text-gray-500">Combined Assigned</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">{data.total_started ?? 0}</div>
            <div className="text-sm text-gray-500">Makeup Started</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{data.total_completed}</div>
            <div className="text-sm text-gray-500">Combined Completed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{data.total_not_completed}</div>
            <div className="text-sm text-gray-500">Combined Not Completed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{data.total_assigned ? `${data.completion_rate}%` : 'No assigned records'}</div>
            <div className="text-sm text-gray-500">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center space-x-4 text-sm text-gray-600">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-50 border border-green-200 mr-2"></div>
          <span>Complete</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-yellow-50 border border-yellow-200 mr-2"></div>
          <span>Partial</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-gray-50 border border-gray-200 mr-2"></div>
          <span>Missing</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-50 border border-red-200 mr-2"></div>
          <span>Needs Review</span>
        </div>
        <div className="ml-auto">
          <span className="text-gray-500">L = Live, M = Makeup, E = External</span>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                  Student
                </th>
                {data.dates.map((date) => (
                  <th key={date} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {studentEntries.map(([studentId, studentData]) => (
                <tr key={studentId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-white">
                    <Link
                      to={`/v2/teacher/students/${studentData.student_id}/work?section_id=${sectionId}&from=${startDate}&to=${endDate}`}
                      className="text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                    >
                      {studentData.student_last_name}, {studentData.student_first_name}
                    </Link>
                  </td>
                  {data.dates.map((date) => {
                    const dateData = studentData.dates[date];
                    if (!dateData) {
                      return (
                        <td key={date} className="px-4 py-3 text-center">
                          <span className="text-gray-300">-</span>
                        </td>
                      );
                    }

                    const totalAssigned =
                      dateData.live_assigned + dateData.makeup_assigned + dateData.external_assigned;

                    if (totalAssigned === 0 && !dateData.daily_work) {
                      return (
                        <td key={date} className="px-4 py-3 text-center">
                          <span className="text-gray-300">-</span>
                        </td>
                      );
                    }

                    return (
                      <td key={date} className="px-2 py-2">
                        <div
                          className={`border rounded p-2 ${getCellClass(
                            totalAssigned ? dateData.completion_status : dateData.daily_work?.wrapup_status === 'submitted' ? 'complete' : 'partial',
                            dateData.needs_review
                          )}`}
                        >
                          {totalAssigned > 0 && <div className="text-center text-sm font-medium">
                            {getCompletionText(dateData)}
                          </div>}
                          <div className="text-center text-xs text-gray-600 mt-1">
                            {getTypeBreakdown(dateData)}
                          </div>
                          {dateData.daily_work && <div className="mt-2 min-w-[120px] text-xs">
                            <p>Plan: {dailyLabel(dateData.daily_work.plan_status)}</p>
                            <p>Wrap-up: {dailyLabel(dateData.daily_work.wrapup_status)}</p>
                            <details className="mt-1"><summary className="cursor-pointer text-indigo-700 underline">View daily work</summary>
                              <p className="mt-2 whitespace-pre-wrap">Plan: {dateData.daily_work.plan || 'No plan recorded.'}</p>
                              <p className="mt-2 whitespace-pre-wrap">Wrap-up: {dateData.daily_work.wrap_up || 'No wrap-up text recorded.'}</p>
                              {dateData.daily_work.result && <p className="mt-2">Student-reported result: {dateData.daily_work.result.replace(/_/g, ' ')}</p>}
                              {dateData.daily_work.evidence_url && <a className="mt-2 block underline" href={dateData.daily_work.evidence_url} target="_blank" rel="noreferrer">Open evidence link</a>}
                              {dateData.daily_work.attachments?.map(a => <a key={a.url} className="mt-2 block underline" href={a.url} target="_blank" rel="noreferrer">Screenshot: {a.name}</a>)}
                            </details>
                          </div>}
                          {dateData.quality_flags.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1 justify-center">
                              {dateData.quality_flags.slice(0, 2).map((flag) => (
                                <span
                                  key={flag}
                                  className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded"
                                  title={`${studentData.student_last_name}, ${studentData.student_first_name}: ${getQualityFlagLabel(flag)}`}
                                >
                                  {getQualityFlagLabel(flag)}
                                </span>
                              ))}
                              {dateData.quality_flags.length > 2 && (
                                <span 
                                  className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded"
                                  title={`${studentData.student_last_name}, ${studentData.student_first_name}: Additional flags (${dateData.quality_flags.length - 2} more)`}
                                >
                                  +{dateData.quality_flags.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {studentEntries.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No completion data available for this date range
        </div>
      )}

      {assignmentBoards.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assignment Status</h2>
            <p className="text-sm text-gray-600">
              Click a student name to review their saved assignment details.
            </p>
          </div>

          {assignmentBoards.map((board) => (
            <section key={board.assignment_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="border-b border-gray-200 px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{board.lesson_plan_title}</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Assignment {board.assignment_id}
                      {board.report_date ? ` | ${formatDate(board.report_date)}` : ''}
                      {board.due_at ? ` | Due ${formatDateTime(board.due_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs font-medium text-gray-700">
                    <span className="rounded bg-gray-100 px-2 py-1">{board.assigned_count} assigned</span>
                    <span className="rounded bg-amber-100 px-2 py-1">{board.started_count} started</span>
                    <span className="rounded bg-green-100 px-2 py-1">{board.completed_count} complete</span>
                  </div>
                </div>
              </div>

              <div className="grid gap-0 md:grid-cols-4">
                {statusColumns.map((column) => {
                  const students = board.buckets[column.key] || [];
                  return (
                    <div key={`${board.assignment_id}-${column.key}`} className={`min-h-[140px] border-b border-gray-200 p-3 md:border-b-0 md:border-r last:md:border-r-0 ${column.className}`}>
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{column.label}</h4>
                        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-700">
                          {students.length}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {students.length === 0 ? (
                          <p className="text-xs text-gray-500">None</p>
                        ) : (
                          students.map((student) => (
                            <Link
                              key={`${board.assignment_id}-${student.student_id}`}
                              to={`/v2/teacher/assignments/${board.assignment_id}/students/${student.student_id}`}
                              className="block rounded-md bg-white px-3 py-2 text-sm text-gray-800 shadow-sm hover:text-indigo-700 hover:ring-1 hover:ring-indigo-200"
                            >
                              <span className="font-medium">{student.student_name}</span>
                              <span className="mt-1 block text-xs text-gray-500">
                                {student.completed_at
                                  ? `Completed ${formatDateTime(student.completed_at)}`
                                  : student.started_at
                                    ? `Started ${formatDateTime(student.started_at)}`
                                    : 'Not opened yet'}
                                {student.response_count > 0 ? ` | ${student.response_count} response${student.response_count === 1 ? '' : 's'}` : ''}
                              </span>
                            </Link>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyCompletionMatrix;
